'use client';

/**
 * Inbound Routing panel for the org-scoped Phone Numbers tab.
 *
 * Lets an admin configure, per provisioned number:
 *   - per-member routing (a known member rings their assigned case manager)
 *   - the org default answerer (case manager or a raw external number)
 *   - how unknown callers (non-member/non-contact) are handled
 *   - a "redirect all inbound calls" override for when a case manager is out
 *
 * Plus an Unknown Caller Follow-ups queue where non-member callers can be
 * converted into a contact or dismissed.
 *
 * Backend: /api/inbound-call-routing/* (see routers/inbound_call_routing.py)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Phone, PhoneForwarded, UserPlus, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { getWeaveinApiUrl } from '@/src/config';

interface CaseManager {
  id: number;
  name: string | null;
  email: string;
  has_voice_number: boolean;
}

interface RoutingRule {
  phone_number: string;
  configured: boolean;
  route_per_member: boolean;
  default_user_id: number | null;
  default_user_name?: string | null;
  default_external_number: string | null;
  ring_timeout_seconds: number;
  unknown_caller_action: string;
  override_active: boolean;
  override_user_id?: number | null;
  override_user_name?: string | null;
  override_external_number?: string | null;
  override_note?: string | null;
  override_set_at?: string | null;
}

interface FollowUp {
  id: number;
  from_number: string;
  to_number: string;
  status: string;
  caller_type: string | null;
  caller_name: string | null;
  recording_url: string | null;
  duration_seconds: number | null;
  created_ts: string;
}

interface Props {
  organizationId: number | null;
  /** Provisioned org numbers to choose from (E.164). */
  numbers: string[];
}

const formatPhone = (phone: string) => {
  const cleaned = (phone || '').replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

export const InboundRoutingPanel: React.FC<Props> = ({ organizationId, numbers }) => {
  const [selectedNumber, setSelectedNumber] = useState<string>(numbers[0] || '');
  const [rule, setRule] = useState<RoutingRule | null>(null);
  const [caseManagers, setCaseManagers] = useState<CaseManager[]>([]);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Contact-from-call modal
  const [contactFor, setContactFor] = useState<FollowUp | null>(null);
  const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', assigned_to: '' });

  const headers = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
      'X-CBO-ID': String(organizationId || ''),
    }),
    [organizationId],
  );

  useEffect(() => {
    if (numbers.length && !numbers.includes(selectedNumber)) setSelectedNumber(numbers[0]);
  }, [numbers, selectedNumber]);

  const loadCaseManagers = useCallback(async () => {
    if (!organizationId) return;
    try {
      const res = await fetch(getWeaveinApiUrl('/api/inbound-call-routing/case-managers'), { headers: headers() });
      if (res.ok) setCaseManagers((await res.json()).case_managers || []);
    } catch {
      /* non-fatal */
    }
  }, [organizationId, headers]);

  const loadFollowUps = useCallback(async () => {
    if (!organizationId) return;
    try {
      const res = await fetch(getWeaveinApiUrl('/api/inbound-call-routing/follow-ups?status=pending'), {
        headers: headers(),
      });
      if (res.ok) setFollowUps((await res.json()).follow_ups || []);
    } catch {
      /* non-fatal */
    }
  }, [organizationId, headers]);

  const loadRule = useCallback(async () => {
    if (!organizationId || !selectedNumber) {
      setRule(null);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(
        getWeaveinApiUrl(`/api/inbound-call-routing/rule?phone_number=${encodeURIComponent(selectedNumber)}`),
        { headers: headers() },
      );
      if (res.ok) {
        setRule(await res.json());
      } else {
        // Don't hang on the spinner: surface the error and let the user retry.
        setRule(null);
        setLoadError(
          res.status === 404
            ? 'Inbound routing is not available yet for this organization.'
            : `Could not load routing rule (HTTP ${res.status}).`,
        );
      }
    } catch {
      setRule(null);
      setLoadError('Could not reach the server. Check your connection and retry.');
    } finally {
      setLoading(false);
    }
  }, [organizationId, selectedNumber, headers]);

  useEffect(() => {
    loadCaseManagers();
    loadFollowUps();
  }, [loadCaseManagers, loadFollowUps]);
  useEffect(() => {
    loadRule();
  }, [loadRule]);

  const patchRule = (patch: Partial<RoutingRule>) => setRule((r) => (r ? { ...r, ...patch } : r));

  const saveRule = async () => {
    if (!rule) return;
    setSaving(true);
    try {
      const res = await fetch(getWeaveinApiUrl('/api/inbound-call-routing/rule'), {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({
          phone_number: selectedNumber,
          route_per_member: rule.route_per_member,
          default_user_id: rule.default_user_id,
          default_external_number: rule.default_external_number || null,
          ring_timeout_seconds: rule.ring_timeout_seconds,
          unknown_caller_action: rule.unknown_caller_action,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      setRule(await res.json());
      toast.success('Routing rule saved');
    } catch (e: any) {
      toast.error(e.message || 'Failed to save routing rule');
    } finally {
      setSaving(false);
    }
  };

  const setOverride = async (active: boolean) => {
    if (!rule) return;
    if (active && !rule.override_user_id && !rule.override_external_number) {
      toast.error('Pick a case manager or enter a number to redirect to first');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(getWeaveinApiUrl('/api/inbound-call-routing/override'), {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          phone_number: selectedNumber,
          active,
          user_id: rule.override_user_id || null,
          external_number: rule.override_external_number || null,
          note: rule.override_note || null,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Override failed');
      setRule(await res.json());
      toast.success(active ? 'All inbound calls are now redirected' : 'Redirect turned off — normal routing restored');
    } catch (e: any) {
      toast.error(e.message || 'Failed to update override');
    } finally {
      setSaving(false);
    }
  };

  const submitContact = async () => {
    if (!contactFor || !contactForm.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    try {
      const res = await fetch(
        getWeaveinApiUrl(`/api/inbound-call-routing/follow-ups/${contactFor.id}/create-contact`),
        {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({
            first_name: contactForm.first_name.trim(),
            last_name: contactForm.last_name.trim() || null,
            phone_no: contactFor.from_number,
            assigned_to: contactForm.assigned_to ? Number(contactForm.assigned_to) : null,
          }),
        },
      );
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create contact');
      toast.success('Contact created from call');
      setContactFor(null);
      setContactForm({ first_name: '', last_name: '', assigned_to: '' });
      loadFollowUps();
    } catch (e: any) {
      toast.error(e.message || 'Failed to create contact');
    }
  };

  const dismissFollowUp = async (id: number) => {
    try {
      const res = await fetch(getWeaveinApiUrl(`/api/inbound-call-routing/follow-ups/${id}/dismiss`), {
        method: 'POST',
        headers: headers(),
      });
      if (!res.ok) throw new Error('Dismiss failed');
      setFollowUps((f) => f.filter((x) => x.id !== id));
    } catch (e: any) {
      toast.error(e.message || 'Failed to dismiss');
    }
  };

  if (!numbers.length) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-500">
        No provisioned numbers yet. Provision a number first, then configure inbound routing here.
      </div>
    );
  }

  const inputCls = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none';

  return (
    <div className="space-y-8">
      {/* Number selector */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Number to configure</label>
        <select className={inputCls} value={selectedNumber} onChange={(e) => setSelectedNumber(e.target.value)}>
          {numbers.map((n) => (
            <option key={n} value={n}>
              {formatPhone(n)}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400">Loading routing rule…</div>
      ) : loadError || !rule ? (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <span>{loadError || 'Routing rule unavailable.'}</span>
          <button
            onClick={loadRule}
            className="ml-auto rounded-md border border-red-300 px-3 py-1 text-xs text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* Override banner */}
          {rule.override_active && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
              <PhoneForwarded className="mt-0.5 h-5 w-5 text-amber-600" />
              <div className="text-sm text-amber-800">
                <div className="font-semibold">All inbound calls are being redirected</div>
                <div>
                  To{' '}
                  {rule.override_user_name ||
                    (rule.override_external_number && formatPhone(rule.override_external_number)) ||
                    'the override target'}
                  {rule.override_note ? ` — “${rule.override_note}”` : ''}
                  {rule.override_set_at ? ` (since ${new Date(rule.override_set_at).toLocaleString()})` : ''}
                </div>
              </div>
            </div>
          )}

          {/* Routing rule */}
          <section className="rounded-lg border border-gray-200 p-5">
            <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
              <Phone className="h-4 w-4" /> Routing
            </h3>

            <label className="mb-4 flex items-center gap-3">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={rule.route_per_member}
                onChange={(e) => patchRule({ route_per_member: e.target.checked })}
              />
              <span className="text-sm text-gray-700">
                Route known members to their assigned case manager
                <span className="block text-xs text-gray-400">
                  When off, every caller goes to the default answerer below.
                </span>
              </span>
            </label>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Default answerer</label>
                <select
                  className={inputCls}
                  value={rule.default_user_id ?? ''}
                  onChange={(e) =>
                    patchRule({ default_user_id: e.target.value ? Number(e.target.value) : null })
                  }
                >
                  <option value="">— None —</option>
                  {caseManagers.map((cm) => (
                    <option key={cm.id} value={cm.id}>
                      {cm.name || cm.email}
                      {cm.has_voice_number ? '' : ' (no phone — rings app)'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  …or a specific number
                </label>
                <input
                  className={inputCls}
                  placeholder="+18085551234"
                  value={rule.default_external_number ?? ''}
                  onChange={(e) => patchRule({ default_external_number: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Ring timeout (seconds)</label>
                <input
                  type="number"
                  min={5}
                  max={120}
                  className={inputCls}
                  value={rule.ring_timeout_seconds}
                  onChange={(e) => patchRule({ ring_timeout_seconds: Number(e.target.value) })}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Unknown callers</label>
                <select
                  className={inputCls}
                  value={rule.unknown_caller_action}
                  onChange={(e) => patchRule({ unknown_caller_action: e.target.value })}
                >
                  <option value="ring_default">Ring the default answerer</option>
                  <option value="voicemail">Send straight to voicemail</option>
                </select>
              </div>
            </div>

            <button
              onClick={saveRule}
              disabled={saving}
              className="mt-5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save routing'}
            </button>
          </section>

          {/* Override control */}
          <section className="rounded-lg border border-gray-200 p-5">
            <h3 className="mb-1 flex items-center gap-2 text-base font-semibold text-gray-900">
              <PhoneForwarded className="h-4 w-4" /> Redirect all inbound calls
            </h3>
            <p className="mb-4 text-xs text-gray-500">
              Use when the usual case manager is out of the office. Every inbound call on this number goes to the
              target below, ignoring per-member routing.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Redirect to case manager</label>
                <select
                  className={inputCls}
                  value={rule.override_user_id ?? ''}
                  onChange={(e) =>
                    patchRule({ override_user_id: e.target.value ? Number(e.target.value) : null })
                  }
                >
                  <option value="">— None —</option>
                  {caseManagers.map((cm) => (
                    <option key={cm.id} value={cm.id}>
                      {cm.name || cm.email}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">…or a specific number</label>
                <input
                  className={inputCls}
                  placeholder="+18085551234"
                  value={rule.override_external_number ?? ''}
                  onChange={(e) => patchRule({ override_external_number: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Note (optional)</label>
                <input
                  className={inputCls}
                  placeholder="e.g. Malia out until Monday"
                  value={rule.override_note ?? ''}
                  onChange={(e) => patchRule({ override_note: e.target.value })}
                />
              </div>
            </div>
            <button
              onClick={() => setOverride(!rule.override_active)}
              disabled={saving}
              className={`mt-5 rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                rule.override_active ? 'bg-gray-600 hover:bg-gray-700' : 'bg-amber-600 hover:bg-amber-700'
              }`}
            >
              {rule.override_active ? 'Turn off redirect' : 'Redirect all inbound calls'}
            </button>
          </section>
        </>
      )}

      {/* Unknown-caller follow-ups */}
      <section className="rounded-lg border border-gray-200 p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <UserPlus className="h-4 w-4" /> Unknown caller follow-ups
            {followUps.length > 0 && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                {followUps.length}
              </span>
            )}
          </h3>
          <button
            onClick={loadFollowUps}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>

        {followUps.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-400">
            No callers awaiting follow-up. Calls from people who aren’t members or contacts appear here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-gray-400">
                  <th className="py-2 pr-4">Caller</th>
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Voicemail</th>
                  <th className="py-2 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {followUps.map((f) => (
                  <tr key={f.id} className="border-b last:border-0">
                    <td className="py-3 pr-4 font-medium text-gray-800">{formatPhone(f.from_number)}</td>
                    <td className="py-3 pr-4 text-gray-500">{new Date(f.created_ts).toLocaleString()}</td>
                    <td className="py-3 pr-4">
                      {f.recording_url ? (
                        <a href={f.recording_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                          Listen{f.duration_seconds ? ` (${f.duration_seconds}s)` : ''}
                        </a>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setContactFor(f);
                            setContactForm({ first_name: '', last_name: '', assigned_to: '' });
                          }}
                          className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                        >
                          Create contact
                        </button>
                        <button
                          onClick={() => dismissFollowUp(f.id)}
                          className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-600 hover:bg-gray-50"
                        >
                          Dismiss
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Create-contact-from-call modal */}
      {contactFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-lg font-semibold text-gray-900">New contact from call</h4>
              <button onClick={() => setContactFor(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mb-4 text-sm text-gray-500">
              Caller: <span className="font-medium text-gray-700">{formatPhone(contactFor.from_number)}</span>
            </p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">First name *</label>
                <input
                  className={inputCls}
                  value={contactForm.first_name}
                  onChange={(e) => setContactForm((c) => ({ ...c, first_name: e.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Last name</label>
                <input
                  className={inputCls}
                  value={contactForm.last_name}
                  onChange={(e) => setContactForm((c) => ({ ...c, last_name: e.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Assign to</label>
                <select
                  className={inputCls}
                  value={contactForm.assigned_to}
                  onChange={(e) => setContactForm((c) => ({ ...c, assigned_to: e.target.value }))}
                >
                  <option value="">— Unassigned —</option>
                  {caseManagers.map((cm) => (
                    <option key={cm.id} value={cm.id}>
                      {cm.name || cm.email}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setContactFor(null)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitContact}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Create contact
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InboundRoutingPanel;
